import React, { useEffect, useState, useMemo, useCallback } from 'react'
import { CoachAPI } from '../../../api/CoachAPI';
import { useSelector } from 'react-redux';
import { DataGrid, GridToolbarQuickFilter } from '@mui/x-data-grid'
import { isClubStaff, isGuest, isUser } from '../../../util/UserRoles';
import { Tooltip } from '@mui/material';

// ✅ FIX 1: Date format - only show date, no time
const dateFormatOptions = {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
    // ❌ Removed: hour, minute, hour12, timeZone
};

// ✅ Custom Toolbar with built-in quick filter
const CustomToolbar = ({ quickFilterProps }) => {
    return (
        <div style={{ 
            width: '100%',
            backgroundColor: '#fafafa',
            borderBottom: '1px solid #e8e8e8',
            padding: '8px 16px',
        }}>
            <div style={{ 
                display: 'flex', 
                justifyContent: 'flex-start', 
                alignItems: 'center',
            }}>
                <GridToolbarQuickFilter
                    {...quickFilterProps}
                    sx={{
                        '& .MuiInputBase-root': {
                            borderRadius: '8px',
                            height: '38px',
                            backgroundColor: 'white',
                            width: '320px',
                        },
                        '& .MuiInputBase-input': {
                            fontSize: '14px',
                            padding: '0 12px',
                        },
                    }}
                    placeholder="Search by Transaction ID or Title..."
                    debounceMs={400}
                    quickFilterParser={(searchInput) => 
                        searchInput.split(',').map((value) => value.trim())
                    }
                />
            </div>
        </div>
    );
};

const EventHistory = () => {
    const selectedClub = useSelector((store) => store.user.selectedClub);
    const userInfo = useSelector((store) => store.user.userInfo);
    const [scheduleHistories, setScheduleHistories] = useState([]);
    const [isLoading, setIsLoading] = useState(false);
    const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 50 });
    const [totalScheduleHistory, setTotalScheduleHistory] = useState(0);
    const [filterData, setFilterData] = useState({});
    const [filterModel, setFilterModel] = useState({ items: [] });
    
    // ✅ FIX 2: Track sorting state with default sort by scheduleStartTime DESC
    const [sortModel, setSortModel] = useState([
        { field: 'scheduleStartTime', sort: 'desc' }
    ]);

    // ✅ Handle API call with filter and sorting
    const fetchAllScheduleHistory = async (filterText = '', size, pageNum, sortData = null) => {
        try {
            const finalSize = size !== undefined ? size : paginationModel.pageSize;
            const finalPage = pageNum !== undefined ? pageNum : paginationModel.page;
            
            // ✅ Build sort params from sortModel
            let sortParams = {};
            if (sortData) {
                sortParams = sortData;
            } else if (sortModel && sortModel.length > 0) {
                const sort = sortModel[0];
                const fieldMap = {
                    'transactionId': 'transactionId',
                    'subject': 'subject',
                    'scheduleStartTime': 'scheduleStartTime',
                    'scheduleEndTime': 'scheduleEndTime',
                    'scheduleCreatedBy': 'scheduleCreatedBy',
                    'venue': 'venue',
                    'status': 'status'
                };
                sortParams = {
                    name: fieldMap[sort.field] || sort.field,
                    direction: sort.sort.toUpperCase()
                };
            }
         
            setIsLoading(true);
            let response;
            if ((isUser(userInfo) && !isClubStaff(userInfo)) || isGuest(userInfo)) {
                response = await CoachAPI.fetchAllScheduleHistoryByUser(
                    selectedClub.clubId, 
                    userInfo.userId, 
                    finalSize, 
                    finalPage, 
                    filterText || '', 
                    filterData,
                    sortParams  // ✅ Pass sort params
                );
            } else {
                response = await CoachAPI.fetchAllScheduleHistory(
                    selectedClub.clubId, 
                    finalSize, 
                    finalPage, 
                    filterText || '', 
                    filterData,
                    sortParams  // ✅ Pass sort params
                );
            }
            const rows = Array.isArray(response?.Data) ? response.Data : [];
            setScheduleHistories(rows);
            setTotalScheduleHistory(rows[0]?.totalHistory || 0);
        } catch (error) {
            console.log(error);
        } finally {
            setIsLoading(false);
        }
    };

    // ✅ Handle sort model changes
    const handleSortModelChange = useCallback((newSortModel) => {
        setSortModel(newSortModel);
        const filterValue = filterModel?.quickFilterValues?.join(',') || '';
        setPaginationModel(prev => ({ ...prev, page: 0 }));
        
        let sortParams = {};
        if (newSortModel && newSortModel.length > 0) {
            const sort = newSortModel[0];
            const fieldMap = {
                'transactionId': 'transactionId',
                'subject': 'subject',
                'scheduleStartTime': 'scheduleStartTime',
                'scheduleEndTime': 'scheduleEndTime',
                'scheduleCreatedBy': 'scheduleCreatedBy',
                'venue': 'venue',
                'status': 'status'
            };
            sortParams = {
                name: fieldMap[sort.field] || sort.field,
                direction: sort.sort.toUpperCase()
            };
        }
        
        fetchAllScheduleHistory(filterValue, paginationModel.pageSize, 0, sortParams);
    }, [paginationModel.pageSize, filterModel]);

    // ✅ Handle filter changes from DataGrid
    const handleFilterChange = useCallback((newFilterModel) => {
        setFilterModel(newFilterModel);
        
        const quickFilterValue = newFilterModel?.quickFilterValues?.join(',') || '';
        
        setPaginationModel(prev => ({ ...prev, page: 0 }));
        
        // Preserve current sort when filtering
        let sortParams = {};
        if (sortModel && sortModel.length > 0) {
            const sort = sortModel[0];
            const fieldMap = {
                'transactionId': 'transactionId',
                'subject': 'subject',
                'scheduleStartTime': 'scheduleStartTime',
                'scheduleEndTime': 'scheduleEndTime',
                'scheduleCreatedBy': 'scheduleCreatedBy',
                'venue': 'venue',
                'status': 'status'
            };
            sortParams = {
                name: fieldMap[sort.field] || sort.field,
                direction: sort.sort.toUpperCase()
            };
        }
        
        fetchAllScheduleHistory(quickFilterValue, paginationModel.pageSize, 0, sortParams);
    }, [paginationModel.pageSize, sortModel]);

    const getStatusByLabel = (label) => {
        switch (label) {
            case 'Deleted':
                return 'IN-ACTIVE';
            case 'Active':
                return 'ACTIVE';
            case 'Canceled':
                return 'CANCELED';
            default:
                return label;
        }
    }

    // ✅ Initial load and pagination changes with sorting
    useEffect(() => {
        if (selectedClub?.clubId) {
            const filterValue = filterModel?.quickFilterValues?.join(',') || '';
            
            let sortParams = {};
            if (sortModel && sortModel.length > 0) {
                const sort = sortModel[0];
                const fieldMap = {
                    'transactionId': 'transactionId',
                    'subject': 'subject',
                    'scheduleStartTime': 'scheduleStartTime',
                    'scheduleEndTime': 'scheduleEndTime',
                    'scheduleCreatedBy': 'scheduleCreatedBy',
                    'venue': 'venue',
                    'status': 'status'
                };
                sortParams = {
                    name: fieldMap[sort.field] || sort.field,
                    direction: sort.sort.toUpperCase()
                };
            }
            
            fetchAllScheduleHistory(filterValue, paginationModel.pageSize, paginationModel.page, sortParams);
        }
    }, [selectedClub?.clubId, paginationModel.page, paginationModel.pageSize]);

    // ✅ FIX 3 & 4: Updated columns with proper widths, sorting, filter disabled
    const gridColumns = useMemo(() => [
        {
            field: 'transactionId',
            headerName: 'Transaction ID',
            flex: 1.2,
            minWidth: 180,
            maxWidth: 350,
            sortable: true,
            filterable: false,
            valueFormatter: (params) => params?.value || '-',
            renderCell: (params) => (
                <Tooltip title={params.value || '-'} arrow>
                    <span style={{ 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        display: 'block',
                        width: '100%'
                    }}>
                        {params.value || '-'}
                    </span>
                </Tooltip>
            )
        },
        {
            field: 'subject',
            headerName: 'Title',
            flex: 1.2,
            minWidth: 150,
            maxWidth: 300,
            sortable: true,
            filterable: false,
            renderCell: (params) => (
                <Tooltip title={params.value || 'No title'} arrow>
                    <span style={{ 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        display: 'block',
                        width: '100%'
                    }}>
                        {params.value || 'No title'}
                    </span>
                </Tooltip>
            )
        },
        {
            field: 'scheduleStartTime',
            headerName: 'Start Date',
            flex: 1,
            minWidth: 130,
            maxWidth: 200,
            sortable: true,
            filterable: false,
            // ✅ FIX 1: Only show date, no time
            valueFormatter: (params) => {
                return params.value ? new Date(params.value).toLocaleDateString('en-US', dateFormatOptions) : '';
            }
        },
        {
            field: 'scheduleEndTime',
            headerName: 'End Date',
            flex: 1,
            minWidth: 130,
            maxWidth: 200,
            sortable: true,
            filterable: false,
            // ✅ FIX 1: Only show date, no time
            valueFormatter: (params) => {
                return params.value ? new Date(params.value).toLocaleDateString('en-US', dateFormatOptions) : '';
            }
        },
        {
            field: 'scheduleCreatedBy',
            headerName: 'Created By',
            flex: 1,
            minWidth: 140,
            maxWidth: 220,
            sortable: true,
            filterable: false,
            valueFormatter: (params) => params?.value || '',
            renderCell: (params) => (
                <Tooltip title={params.value || ''} arrow>
                    <span style={{ 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        display: 'block',
                        width: '100%'
                    }}>
                        {params.value || ''}
                    </span>
                </Tooltip>
            )
        },
        {
            field: 'venue',
            headerName: 'Location',
            flex: 1,
            minWidth: 140,
            maxWidth: 220,
            sortable: true,
            filterable: false,
            renderCell: (params) => (
                <Tooltip title={params.value || ''} arrow>
                    <span style={{ 
                        whiteSpace: 'nowrap', 
                        overflow: 'hidden', 
                        textOverflow: 'ellipsis',
                        display: 'block',
                        width: '100%'
                    }}>
                        {params.value || ''}
                    </span>
                </Tooltip>
            )
        },
        {
            field: 'status',
            headerName: 'Status',
            flex: 0.8,
            minWidth: 110,
            maxWidth: 160,
            sortable: true,
            filterable: false,
            renderCell: (params) => {
                const val = params.value;
                let statusClass = '';
                let displayText = val;

                if (val === 'ACTIVE') {
                    statusClass = 'succeeded';
                    displayText = 'ACTIVE';
                } else if (val === 'IN_PROGRESS') {
                    statusClass = 'processing';
                    displayText = 'IN PROGRESS';
                } else if (val === 'IN-ACTIVE') {
                    statusClass = 'canceled';
                    displayText = 'DELETED';
                } else if (val === 'CANCELED') {
                    statusClass = 'canceled';
                    displayText = 'CANCELED';
                }

                return (
                    <div style={{ display: 'flex', alignItems: 'center' }}>
                        <span style={{
                            padding: '4px 12px',
                            borderRadius: '12px',
                            fontSize: '12px',
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            backgroundColor: statusClass === 'succeeded' ? '#e6f7e6' : 
                                          statusClass === 'processing' ? '#fff3e0' : 
                                          '#fbe9e7',
                            color: statusClass === 'succeeded' ? '#2e7d32' : 
                                   statusClass === 'processing' ? '#e65100' : 
                                   '#c62828'
                        }}>
                            {displayText}
                        </span>
                    </div>
                );
            }
        }
    ], []);

    return (
        <div style={{
            padding: '20px',
            background: '#f9fafb',
            minHeight: '100vh'
        }}>
            <div style={{
                height: 'calc(100vh - 200px)',
                width: '100%',
                background: 'white',
                borderRadius: '8px',
                padding: '16px',
                boxShadow: '0 1px 3px rgba(0,0,0,0.08)'
            }}>
                <DataGrid
                    rows={scheduleHistories}
                    columns={gridColumns}
                    pagination
                    paginationMode="server"
                    rowCount={totalScheduleHistory}
                    paginationModel={paginationModel}
                    onPaginationModelChange={(newModel) => {
                        setPaginationModel(newModel);
                    }}
                    pageSizeOptions={[50, 100]}
                    loading={isLoading}
                    disableSelectionOnClick
                    getRowId={(row) => row.scheduleId || row.transactionId || String(row.index)}
                    
                    // ✅ Sorting
                    sortModel={sortModel}
                    onSortModelChange={handleSortModelChange}
                    
                    // ✅ Filter handling
                    filterModel={filterModel}
                    onFilterModelChange={handleFilterChange}
                    
                    // ✅ Ensure column resizing is enabled
                    disableColumnResize={false}
                    
                    slots={{
                        toolbar: () => (
                            <CustomToolbar
                                quickFilterProps={{
                                    debounceMs: 400,
                                }}
                            />
                        ),
                    }}
                    hideFooterPagination={false}
                    sx={{
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: '#f5f5f5',
                            borderBottom: '2px solid #e8e8e8',
                            fontWeight: 600,
                            color: '#333',
                            fontSize: '14px',
                            textTransform: 'uppercase',
                        },
                        '& .MuiDataGrid-columnHeader': {
                            padding: '8px 16px',
                        },
                        // ✅ Clean column separator - let MUI handle resizing
                        '& .MuiDataGrid-columnSeparator': {
                            visibility: 'visible',
                            color: '#d1d1d1',
                        },
                        '& .MuiDataGrid-cell': {
                            borderBottom: '1px solid #f0f0f0',
                            padding: '8px 16px',
                            fontSize: '14px',
                            color: '#333',
                        },
                        '& .MuiDataGrid-row:hover': {
                            backgroundColor: '#f5f9ff',
                        },
                        '& .MuiDataGrid-footerContainer': {
                            borderTop: '1px solid #e8e8e8',
                            padding: '8px 16px',
                            backgroundColor: '#fafafa',
                        },
                        '& .MuiTablePagination-root': {
                            color: '#666',
                        },
                        '& .MuiDataGrid-toolbarContainer': {
                            padding: 0,
                        },
                        border: 'none',
                        borderRadius: '8px',
                        // ✅ Hide column menu icon
                        '& .MuiDataGrid-menuIcon': {
                            display: 'none !important',
                        },
                        '& .MuiDataGrid-columnHeader .MuiDataGrid-menuIcon': {
                            display: 'none !important',
                        },
                        '& .MuiDataGrid-columnHeader:focus': {
                            outline: 'none',
                        },
                    }}
                />
            </div>
        </div>
    )
}

export default EventHistory